import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { PanelRightClose, Sparkles } from 'lucide-react'
import type { MeetingSummary, TranscriptSegment } from '@hub/contracts'

/**
 * The right-hand "Live" panel (PLAN-085 §1.5): the transcript as Whisper
 * produces it, and the current rolling summary. Read-only — the document is
 * where anything worth keeping goes. The header's close button and the
 * control bar's panel button toggle the same state (board m5gc #6).
 */
export function LivePanel({
  segments,
  summary,
  startedAt,
  onClose,
}: {
  segments: TranscriptSegment[]
  summary: MeetingSummary | null
  startedAt: string | null
  onClose: () => void
}) {
  const { t } = useTranslation('meet')
  const scrollRef = useRef<HTMLDivElement>(null)
  const pinnedToBottom = useRef(true)

  // Follow the transcript, unless the reader has scrolled up to read back.
  useEffect(() => {
    const el = scrollRef.current
    if (!el || !pinnedToBottom.current) return
    el.scrollTop = el.scrollHeight
  }, [segments])

  return (
    // On a phone the panel covers the document instead of squeezing it to a
    // column of letters; the header's close button is the way back.
    <aside className="border-border bg-background absolute inset-y-0 right-0 z-20 flex w-full flex-col border-l md:static md:w-80">
      <header className="border-border flex items-center gap-2 border-b px-4 py-3">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
        </span>
        <h2 className="flex-1 text-sm font-semibold">{t('panel.title')}</h2>
        <button
          type="button"
          aria-label={t('controls.panelOn')}
          title={t('controls.panelOn')}
          data-testid="panel-close"
          onClick={onClose}
          className="text-muted-foreground hover:bg-accent hover:text-foreground -mr-1.5 flex h-7 w-7 items-center justify-center rounded-md transition-colors"
        >
          <PanelRightClose className="h-4 w-4" />
        </button>
      </header>

      <section className="border-border border-b p-4">
        <h3 className="text-muted-foreground mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase">
          <Sparkles className="h-3 w-3" />
          {t('panel.summary')}
        </h3>
        {summary ? (
          <div className="space-y-3 text-sm">
            <p className="leading-relaxed">{summary.summary}</p>
            {summary.decisions.length > 0 && (
              <div>
                <p className="text-muted-foreground text-xs font-semibold">
                  {t('panel.decisions')}
                </p>
                <ul className="mt-1 list-disc space-y-0.5 pl-4">
                  {summary.decisions.map((decision, i) => (
                    <li key={i}>{decision}</li>
                  ))}
                </ul>
              </div>
            )}
            {summary.actions.length > 0 && (
              <div>
                <p className="text-muted-foreground text-xs font-semibold">{t('panel.actions')}</p>
                <ul className="mt-1 list-disc space-y-0.5 pl-4">
                  {summary.actions.map((action, i) => (
                    <li key={i}>
                      {action.text}
                      {action.owner && (
                        <span className="text-muted-foreground"> — {action.owner}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">{t('panel.noSummary')}</p>
        )}
      </section>

      <div
        ref={scrollRef}
        onScroll={(e) => {
          const el = e.currentTarget
          pinnedToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40
        }}
        className="flex-1 space-y-3 overflow-y-auto p-4"
      >
        <h3 className="text-muted-foreground text-xs font-semibold uppercase">
          {t('panel.transcript')}
        </h3>
        {segments.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('panel.empty')}</p>
        ) : (
          segments.map((segment) =>
            segment.marker ? (
              // HUB's own pause/resume line — a divider, not speech.
              <p
                key={segment.seq}
                className="text-muted-foreground border-border my-1 border-y py-1 text-center text-xs italic"
              >
                <span className="mr-1.5 tabular-nums">{formatOffset(segment.startMs)}</span>
                {segment.text.replace(/^\[|\]$/g, '')}
              </p>
            ) : (
              <p key={segment.seq} className="text-sm leading-relaxed">
                <span className="text-muted-foreground mr-1.5 text-xs tabular-nums">
                  {formatOffset(segment.startMs)}
                </span>
                <span className="font-medium">{segment.speaker}</span>{' '}
                <span className="text-foreground/90">{segment.text}</span>
              </p>
            )
          )
        )}
        <span className="sr-only">{startedAt}</span>
      </div>
    </aside>
  )
}

/** `mm:ss` since the meeting started. */
export function formatOffset(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}
